/**
 * Google Analytics 4 event tracking utilities
 * 
 * Usage:
 *   import { trackEvent } from '@/lib/analytics'
 *   trackEvent('cta_click', { button_text: 'Start Free Today', location: 'hero' })
 */

type GtagEvent = {
  action: string
  category?: string
  label?: string
  value?: number
  [key: string]: string | number | undefined
}

/**
 * Track a custom event in Google Analytics
 */
export function trackEvent(
  eventName: string,
  parameters?: Record<string, string | number | boolean>
) {
  if (typeof window === 'undefined') return
  if (!window.gtag) return

  window.gtag('event', eventName, parameters)
}

/**
 * Track a CTA button click
 */
export function trackCtaClick(buttonText: string, location: string) {
  trackEvent('cta_click', {
    button_text: buttonText,
    location: location,
  })
}

/**
 * Track demo button click
 */
export function trackDemoClick() {
  trackEvent('demo_click', {
    location: 'hero',
  })
}

/**
 * Track signup button click
 */
export function trackSignupClick(location: string) {
  trackEvent('signup_click', {
    location: location,
  })
}

// ============================================
// Conversion Events
// ============================================

/**
 * Track when a user completes account signup
 * Sends both GA4 standard event and Google Ads conversion event
 */
export function trackSignupComplete(method?: string) {
  const params = { method: method || 'email' }
  
  console.log('[Analytics] trackSignupComplete called with method:', method)
  console.log('[Analytics] Sending events: sign_up + conversion_event_signup')
  
  // GA4 standard event (for GA4 funnels/reports)
  trackEvent('sign_up', params)
  
  // Google Ads conversion event (for ad campaign tracking)
  trackEvent('conversion_event_signup', params)
}

/**
 * Track when a user creates/starts an artist profile
 */
export function trackArtistCreated(artistHandle: string) {
  trackEvent('artist_created', {
    artist_handle: artistHandle,
  })
}

/**
 * Track when a user discovers/browses artists
 */
export function trackArtistDiscovery(source: 'discover_page' | 'search' | 'direct_link') {
  trackEvent('artist_discovery', {
    source: source,
  })
}

/**
 * Track when a user views an artist's page
 */
export function trackArtistPageView(artistHandle: string, hasLiveEvent: boolean) {
  trackEvent('artist_page_view', {
    artist_handle: artistHandle,
    has_live_event: hasLiveEvent,
  })
}

/**
 * Track when a user joins a live event (scans QR / visits live page)
 */
export function trackLiveEventJoin(artistHandle: string, eventId: string) {
  trackEvent('live_event_join', {
    artist_handle: artistHandle,
    event_id: eventId,
  })
}

/**
 * Track when a user sends a tip
 */
export function trackTipSent(artistHandle: string, amountCents: number) {
  trackEvent('tip_sent', {
    artist_handle: artistHandle,
    value: amountCents / 100, // GA4 expects value in dollars
    currency: 'USD',
  })
}

/**
 * Track when a user requests a song
 */
export function trackSongRequest(artistHandle: string, withTip: boolean) {
  trackEvent('song_request', {
    artist_handle: artistHandle,
    with_tip: withTip,
  })
}

/**
 * Track when a user sends a reaction
 */
export function trackReactionSent(artistHandle: string, reactionType: string) {
  trackEvent('reaction_sent', {
    artist_handle: artistHandle,
    reaction_type: reactionType,
  })
}

// ============================================
// User Identification (for GA4)
// ============================================

/**
 * Set the user ID in Google Analytics
 * This connects anonymous and authenticated sessions
 */
export function setUserId(userId: string) {
  if (typeof window === 'undefined') return
  if (!window.gtag) return

  window.gtag('set', { user_id: userId })
}

/**
 * Clear the user ID from Google Analytics
 * Call this on sign-out
 */
export function clearUserId() {
  if (typeof window === 'undefined') return
  if (!window.gtag) return

  window.gtag('set', { user_id: undefined })
}

// ============================================
// User Properties (for GA4 Segmentation)
// ============================================

/**
 * Set a user property in Google Analytics
 * User properties persist across sessions and can be used for segmentation in GA4 reports
 * 
 * NOTE: You must register custom user properties in GA4 Admin:
 * Admin → Data display → Custom definitions → Custom user properties
 */
export function setUserProperty(name: string, value: string | undefined) {
  if (typeof window === 'undefined') return
  if (!window.gtag) return

  window.gtag('set', 'user_properties', {
    [name]: value,
  })
}

/**
 * Set the internal visitor ID as a GA4 user property
 * This enables correlation between GA4 data and internal analytics
 */
export function setGAVisitorId(visitorId: string) {
  setUserProperty('visitor_id', visitorId)
}

/**
 * Set the user type for segmentation (authenticated vs visitor)
 * Use this to analyze behavior differences between logged-in users and anonymous visitors
 */
export function setUserType(type: 'authenticated' | 'visitor') {
  setUserProperty('user_type', type)
}

// ============================================
// Page Analytics Events (GA4 mirroring)
// ============================================

/**
 * Track extended page view with engagement metrics
 */
export function trackPageViewExtended(
  path: string,
  scrollDepth: number,
  timeOnPageMs: number,
  engagementScore: number
) {
  trackEvent('page_view_extended', {
    page_path: path,
    scroll_depth: scrollDepth,
    time_on_page_ms: timeOnPageMs,
    engagement_score: Math.round(engagementScore),
  })
}

/**
 * Track scroll milestone (25/50/75/100)
 */
export function trackScrollMilestone(path: string, milestone: number) {
  trackEvent('scroll_milestone', {
    page_path: path,
    milestone,
  })
}

/**
 * Track analytics session start
 */
export function trackAnalyticsSessionStart(sessionToken: string, isNew: boolean) {
  trackEvent('analytics_session_start', {
    session_token: sessionToken,
    is_new_session: isNew,
  })
}

/**
 * Track analytics session end
 */
export function trackAnalyticsSessionEnd(
  sessionToken: string,
  durationMs: number,
  pageCount: number
) {
  trackEvent('analytics_session_end', {
    session_token: sessionToken,
    duration_ms: durationMs,
    page_count: pageCount,
  })
}

// Extend Window interface for gtag
declare global {
  interface Window {
    gtag?: (
      command: 'event' | 'config' | 'set',
      targetId: string | Record<string, string | number | boolean | undefined>,
      config?: GtagEvent | Record<string, string | number | boolean | undefined>
    ) => void
  }
}


